import { useState, useCallback } from 'react';
import './Faq.css';
import { Contact } from '../../components/Contact/Contact';
import ContactModal from '../../modals/ContactModal/ContactModal';

// ─── Data ─────────────────────────────────────────────────────────────────────

const CATEGORIES = [
    {
        id: 'getting-started',
        label: 'Getting started',
        color: 'blue',
        count: 4,
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
            </svg>
        ),
        faqs: [
            {
                q: 'What is Qinto and how does it work?',
                a: 'Qinto is an AI-powered classifieds marketplace for India. You can post ads to sell items, browse listings near you, and chat directly with sellers — all without sharing your phone number publicly.',
            },
            {
                q: 'Is Qinto free to use?',
                a: "Yes — browsing listings and posting a basic ad is completely free. We may offer optional paid boosts in the future to increase your listing's visibility, but the core experience will always be free.",
            },
            {
                q: 'Do I need an account to browse listings?',
                a: 'No — you can browse all listings without an account. You only need to sign up when you want to post an ad or start a chat with a seller.',
            },
            {
                q: 'Is there a mobile app available?',
                a: 'Yes! The Qinto app is available on Google Play. An iOS version is in the works. The app gives you push notifications for new messages and buyer enquiries.',
            },
        ],
    },
    {
        id: 'posting-ads',
        label: 'Posting ads',
        color: 'teal',
        count: 5,
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M12 8v8M8 12h8" />
            </svg>
        ),
        faqs: [
            {
                q: 'How do I post a new ad?',
                a: 'Tap "Post a free ad", choose a category, upload photos, enter a price and description. Our AI will suggest an optimised title and description — you can use it as-is or edit it.',
            },
            {
                q: 'What does the AI listing assistant do?',
                a: 'After you upload a photo and enter a brief description, our AI generates a compelling title, a detailed description, and suggests relevant tags — all designed to help your listing rank higher and attract serious buyers.',
            },
            {
                q: 'How long does my ad stay active?',
                a: "Ads are active for 60 days by default. You'll get a reminder before expiry so you can renew with one tap. Sold items can be marked as sold at any time.",
            },
            {
                q: 'Can I edit or delete my ad after posting?',
                a: 'Yes — go to "My Ads" in your profile. You can edit the title, description, price, or photos at any time. To remove an ad, tap the three-dot menu and select Delete.',
            },
            {
                q: 'How many photos can I add to a listing?',
                a: 'You can add up to 8 photos per listing. Listings with 3 or more photos get significantly more views, so we recommend uploading as many clear images as possible.',
            },
        ],
    },
    {
        id: 'buying',
        label: 'Buying',
        color: 'amber',
        count: 4,
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
        ),
        faqs: [
            {
                q: 'How do I contact a seller?',
                a: 'Open any listing and tap "Chat with seller". You\'ll need to be logged in. The seller gets notified instantly and can reply from their app or the website.',
            },
            {
                q: 'Does Qinto handle payments or delivery?',
                a: 'Currently Qinto is a listing and chat platform — payments and delivery are arranged directly between buyer and seller. We recommend meeting in a public place and paying only on handover.',
            },
            {
                q: 'How do I search for items near me?',
                a: 'Use the location filter on the search page to set your city or a radius around your current location. The app uses your device GPS to automatically show nearby listings by default.',
            },
            {
                q: 'How do I report a suspicious listing?',
                a: 'Tap the flag icon on any listing to report it. Our moderation team reviews all reports within 24 hours. You can also block a user from within any chat thread.',
            },
        ],
    },
    {
        id: 'chat-safety',
        label: 'Chat & safety',
        color: 'coral',
        count: 4,
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
        ),
        faqs: [
            {
                q: 'Is my phone number visible to other users?',
                a: 'No — your phone number is never shown publicly. All communication happens inside the Qinto chat. You choose if and when to share any personal contact details.',
            },
            {
                q: 'Can I block or report another user?',
                a: "Yes. Open the chat thread, tap the user's name at the top, and choose Block or Report. Blocked users can no longer message you or view your listings.",
            },
            {
                q: 'What should I watch out for when meeting a buyer or seller?',
                a: 'Always meet in a well-lit public place (a café, mall, or busy street). Never transfer money before receiving the item. Be cautious of deals that seem too good to be true.',
            },
            {
                q: 'How do I delete a conversation?',
                a: "Swipe left on a chat thread in your inbox and tap Delete. This removes it from your view only — the other user's copy is unaffected.",
            },
        ],
    },
    {
        id: 'account',
        label: 'Account',
        color: 'purple',
        count: 3,
        icon: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
            </svg>
        ),
        faqs: [
            {
                q: 'How do I sign up?',
                a: 'Tap "Post a free ad" or "Chat with seller" and you\'ll be prompted to sign up. Enter your mobile number, verify via OTP, and you\'re in — no passwords needed.',
            },
            {
                q: 'How do I change my profile details?',
                a: 'Go to your Profile tab, tap Edit Profile. You can update your display name, profile photo, and city. Your mobile number is your login identifier and cannot be changed.',
            },
            {
                q: 'How do I delete my account?',
                a: 'Go to Profile → Settings → Delete Account. All your ads and chats will be permanently removed within 30 days. This action cannot be undone.',
            },
        ],
    },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function ChevronIcon({ open }) {
    return (
        <svg
            className={`faq-chevron${open ? ' open' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M6 9l6 6 6-6" />
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg
            className="search-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.35-4.35" />
        </svg>
    );
}

function FaqItem({ question, answer, isOpen, onToggle }) {
    return (
        <div className="faq-item">
            <button
                className={`faq-q${isOpen ? ' open' : ''}`}
                onClick={onToggle}
                aria-expanded={isOpen}
            >
                <span className="faq-q-text">{question}</span>
                <ChevronIcon open={isOpen} />
            </button>
            <div className={`faq-a${isOpen ? ' open' : ''}`} aria-hidden={!isOpen}>
                <p className="faq-a-text">{answer}</p>
            </div>
        </div>
    );
}

function CategoryBlock({ category, openItems, onToggle, searchQuery }) {
    const filtered = searchQuery
        ? category.faqs.filter(
            (f) =>
                f.q.toLowerCase().includes(searchQuery) ||
                f.a.toLowerCase().includes(searchQuery)
        )
        : category.faqs;

    if (filtered.length === 0) return null;

    return (
        <div className="faq-cat-block" id={category.id}>
            <div className="faq-cat-header">
                <div className={`faq-cat-icon ${category.color}`}>{category.icon}</div>
                <div className="faq-cat-title">{category.label}</div>
            </div>
            <div className="faq-list">
                {filtered.map((faq, idx) => {
                    const key = `${category.id}-${idx}`;
                    return (
                        <FaqItem
                            key={key}
                            question={faq.q}
                            answer={faq.a}
                            isOpen={!!openItems[key]}
                            onToggle={() => onToggle(key)}
                        />
                    );
                })}
            </div>
        </div>
    );
}



// ─── Main Component ───────────────────────────────────────────────────────────

export default function FaqPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [openItems, setOpenItems] = useState({ 'getting-started-0': true });
    const [activeCategory, setActiveCategory] = useState('getting-started');

    const [isModalOpen, setModalOpen] = useState(false);

    const handleToggle = useCallback((key) => {
        setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const handleSearch = (e) => {
        const val = e.target.value.toLowerCase().trim();
        setSearchQuery(val);
        if (val) {
            // Auto-expand all matching items
            const newOpen = {};
            CATEGORIES.forEach((cat) => {
                cat.faqs.forEach((faq, idx) => {
                    if (
                        faq.q.toLowerCase().includes(val) ||
                        faq.a.toLowerCase().includes(val)
                    ) {
                        newOpen[`${cat.id}-${idx}`] = true;
                    }
                });
            });
            setOpenItems(newOpen);
        }
    };

    const handleSidebarClick = (catId) => {
        setActiveCategory(catId);
        const el = document.getElementById(catId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="faq-page">
            {/* ── Nav ── */}
            <nav className="q-nav">
                <a href="/" className="q-nav-logo">
                    Q<span>i</span>nto
                </a>
                <ul className="q-nav-links">
                    <li>
                        <a href="/">Home</a>
                    </li>
                    <li>
                        <a href="/about">About</a>
                    </li>
                    <li>
                        <a href="/faq" className="active">
                            FAQ
                        </a>
                    </li>
                </ul>
                <a href="/post" className="q-nav-cta">
                    Post a free ad
                </a>
            </nav>

            {/* ── Hero ── */}
            <div className="faq-hero">
                <div className="faq-hero-eyebrow">Help centre</div>
                <h1 className="faq-hero-title">How can we help you?</h1>
                <p className="faq-hero-sub">
                    Find answers to the most common questions about buying, selling, and
                    staying safe on Qinto.
                </p>
                <div className="faq-search-wrap">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Search questions… e.g. how to delete an ad"
                        onChange={handleSearch}
                        aria-label="Search FAQ"
                    />
                </div>
            </div>

            {/* ── Body ── */}
            <div className="faq-body">
                {/* Sidebar */}
                <aside className="faq-sidebar">
                    <div className="sidebar-label">Categories</div>
                    {CATEGORIES.map((cat) => (
                        <button
                            key={cat.id}
                            className={`sidebar-item${activeCategory === cat.id ? ' active' : ''
                                }`}
                            onClick={() => handleSidebarClick(cat.id)}
                        >
                            <span className="sidebar-dot" />
                            {cat.label}
                            <span className="sidebar-count">{cat.count}</span>
                        </button>
                    ))}
                </aside>

                {/* Content */}
                <main className="faq-content">
                    {searchQuery && (
                        <div className="search-results-note">
                            Showing results for "<strong>{searchQuery}</strong>"
                        </div>
                    )}
                    {CATEGORIES.map((cat) => (
                        <CategoryBlock
                            key={cat.id}
                            category={cat}
                            openItems={openItems}
                            onToggle={handleToggle}
                            searchQuery={searchQuery}
                        />
                    ))}
                    {searchQuery &&
                        CATEGORIES.every(
                            (cat) =>
                                !cat.faqs.some(
                                    (f) =>
                                        f.q.toLowerCase().includes(searchQuery) ||
                                        f.a.toLowerCase().includes(searchQuery)
                                )
                        ) && (
                            <div className="no-results">
                                <p>
                                    No questions match "<strong>{searchQuery}</strong>".
                                </p>
                                <p>Try different keywords or contact our support team below.</p>
                            </div>
                        )}
                </main>
            </div>

            {/* ── Contact band ── */}
            <div className="contact-band">
                <div className="contact-band-left">
                    <div className="contact-band-title">Still have a question?</div>
                    <div className="contact-band-sub">
                        Our support team usually replies within a few hours.
                    </div>
                </div>
                <div className="contact-btns">
                    <a href="https://play.google.com/store/apps/details?id=com.qinto.qintoapp" target="_blank" rel="noopener noreferrer" className="contact-btn">Browse the app</a>

                    {/* Changed from <a> to <button> for better accessibility */}
                    <button
                        onClick={() => setModalOpen(true)}
                        className="contact-btn primary"
                    >
                        Contact support
                    </button>
                </div>

                <ContactModal
                    isOpen={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    type="right" // Change to "center" if preferred
                >
                    <Contact />
                </ContactModal>
            </div>
        </div>
    );
}
